import { IFareBand, IFareInfo } from "../../interface/car-service";

// ---------- ขั้นบันไดค่าบริการ (fareBands — ใช้คิดเงินจริง) ----------

export interface IBandRowDraft { toKm: string; ratePerKm: string }
export interface IBandDraft {
  baseDistanceKm: string;
  startPrice: string;
  rows: IBandRowDraft[];      // ช่วง จาก–ถึง (ไม่รวมช่วง "ขึ้นไป")
  openEndedRate: string;      // เรตของช่วงสุดท้าย "ขึ้นไป" (มีเสมอ ลบไม่ได้)
}
export interface IRowError { toKm?: string; ratePerKm?: string }
export interface IBandErrors {
  baseDistanceKm?: string;
  startPrice?: string;
  rows: IRowError[];
  openEndedRate?: string;
}
export interface ILegalCaps {
  baseFareLegalMin: number | null;
  baseFareLegalMax: number | null;
  perKmLegalMin: number | null;
  perKmLegalMax: number | null;
}

export const round01 = (v: number): number => Math.round(v * 10) / 10;

// mirror ของ isKmStep01 ฝั่ง backend (epsilon 1e-6 บน v*10)
export const isKmStep01 = (v: number): boolean => {
  const r = v * 10;
  return Math.abs(r - Math.round(r)) < 1e-6;
};

// Number("") === 0 — ต้องกันช่องว่างให้เป็น NaN
const parseNum = (s: string): number => (s.trim() === "" ? NaN : Number(s));

// จาก (fromKm) ของทุกช่วง รวมช่วง "ขึ้นไป" ท้ายสุด — derived เสมอ ไม่เก็บใน state (D1)
// คืน NaN ต่อช่วงที่ค่าก่อนหน้ายังไม่ valid
export const deriveBandFroms = (draft: IBandDraft): number[] => {
  const froms: number[] = [];
  let prev = parseNum(draft.baseDistanceKm);
  for (const row of draft.rows) {
    froms.push(round01(prev + 0.1));
    prev = parseNum(row.toKm);
  }
  froms.push(round01(prev + 0.1));
  return froms;
};

const rateError = (raw: string, caps: ILegalCaps | null): string | undefined => {
  const v = parseNum(raw);
  if (isNaN(v) || v <= 0) return "ต้องมากกว่า 0";
  if (caps?.perKmLegalMin != null && v < caps.perKmLegalMin)
    return `ต่ำกว่าประกาศกำหนดขั้นต่ำ ${caps.perKmLegalMin}`;
  if (caps?.perKmLegalMax != null && v > caps.perKmLegalMax)
    return `เกินประกาศกำหนดสูงสุด ${caps.perKmLegalMax}`;
  return undefined;
};

// กติกา mirror ฝั่ง backend ValidateFareBands + ValidateFareAgainstLegalCaps (spec §2)
export const validateBandDraft = (draft: IBandDraft, caps: ILegalCaps | null): IBandErrors => {
  const errors: IBandErrors = { rows: draft.rows.map(() => ({})) };

  const base = parseNum(draft.baseDistanceKm);
  if (isNaN(base) || base <= 0) errors.baseDistanceKm = "ต้องมากกว่า 0";
  else if (!isKmStep01(base)) errors.baseDistanceKm = "ต้องเป็นความละเอียด 0.1 กม.";

  const sp = parseNum(draft.startPrice);
  if (isNaN(sp) || sp < 0) errors.startPrice = "ต้องเป็นตัวเลขตั้งแต่ 0";
  else if (caps?.baseFareLegalMin != null && sp < caps.baseFareLegalMin)
    errors.startPrice = `ต่ำกว่าประกาศกำหนดขั้นต่ำ ${caps.baseFareLegalMin}`;
  else if (caps?.baseFareLegalMax != null && sp > caps.baseFareLegalMax)
    errors.startPrice = `เกินประกาศกำหนดสูงสุด ${caps.baseFareLegalMax}`;

  const froms = deriveBandFroms(draft);
  draft.rows.forEach((row, i) => {
    const to = parseNum(row.toKm);
    if (isNaN(to)) errors.rows[i].toKm = "จำเป็นต้องระบุ";
    else if (!isKmStep01(to)) errors.rows[i].toKm = "ต้องเป็นความละเอียด 0.1 กม.";
    else if (!isNaN(froms[i]) && to <= froms[i])
      errors.rows[i].toKm = `ต้องมากกว่า ${froms[i].toFixed(1)}`;
    errors.rows[i].ratePerKm = rateError(row.ratePerKm, caps);
  });
  errors.openEndedRate = rateError(draft.openEndedRate, caps);
  return errors;
};

export const hasBandErrors = (e: IBandErrors): boolean =>
  !!(e.baseDistanceKm || e.startPrice || e.openEndedRate) ||
  e.rows.some((r) => !!(r.toKm || r.ratePerKm));

// สร้าง payload fareBands — เรียกหลัง validate ผ่านแล้วเท่านั้น
// sentinel: ช่วงสุดท้าย toKm = 0 (ไม่ใช่ null — นั่นของ fareInfo)
export const buildFareBands = (draft: IBandDraft): IFareBand[] => {
  const froms = deriveBandFroms(draft);
  const bands: IFareBand[] = draft.rows.map((row, i) => ({
    fromKm: froms[i],
    toKm: round01(parseNum(row.toKm)),
    ratePerKm: Number(row.ratePerKm),
  }));
  bands.push({
    fromKm: froms[froms.length - 1],
    toKm: 0,
    ratePerKm: Number(draft.openEndedRate),
  });
  return bands;
};

// โหลดค่าจาก GET → draft; รองรับ document เก่าที่ fareBands ยัง null (pre-migration):
// เริ่มด้วยช่วง "ขึ้นไป" ช่วงเดียว เรตจาก chargePerKilometer เดิม — save ครั้งแรกจะ convert
export const bandsToDraft = (svc: {
  fareBands: IFareBand[] | null;
  baseDistanceKm: number;
  startPrice: number;
  chargePerKilometer: number;
}): IBandDraft => {
  const bands = svc.fareBands ?? [];
  if (bands.length === 0) {
    return {
      baseDistanceKm: svc.baseDistanceKm > 0 ? String(svc.baseDistanceKm) : "1",
      startPrice: String(svc.startPrice ?? 0),
      rows: [],
      openEndedRate: svc.chargePerKilometer > 0 ? String(svc.chargePerKilometer) : "",
    };
  }
  const finite = bands.slice(0, -1);
  const last = bands[bands.length - 1];
  return {
    baseDistanceKm: String(svc.baseDistanceKm),
    startPrice: String(svc.startPrice),
    rows: finite.map((b) => ({ toKm: String(b.toKm), ratePerKm: String(b.ratePerKm) })),
    openEndedRate: String(last.ratePerKm),
  };
};

// ---------- fareInfo (display-only — เนื้อหาจอรายละเอียดฝั่งผู้โดยสาร) ----------

export interface ITierDraft { fromKm: string; toKm: string; pricePerKm: string }
export interface IFareInfoDraft {
  startPriceText: string;
  tiers: ITierDraft[];
  trafficRateText: string;
  conditions: string[];
}
export interface ITierError { fromKm?: string; toKm?: string; pricePerKm?: string }

export const fareInfoToDraft = (fi: IFareInfo | null | undefined): IFareInfoDraft => ({
  startPriceText: fi?.startPriceText ?? "",
  tiers: (fi?.rateTiers ?? []).map((t) => ({
    fromKm: String(t.fromKm),
    toKm: t.toKm == null ? "" : String(t.toKm),
    pricePerKm: String(t.pricePerKm),
  })),
  trafficRateText: fi?.trafficRateText ?? "",
  conditions: [...(fi?.conditions ?? [])],
});

// mirror ฝั่ง backend FareInfo.Validate — ไม่มีข้อบังคับ 0.1-step และไม่บังคับต่อเนื่อง
export const validateFareInfoDraft = (d: IFareInfoDraft): ITierError[] => {
  const errors: ITierError[] = d.tiers.map(() => ({}));
  d.tiers.forEach((tier, i) => {
    const from = parseNum(tier.fromKm);
    const price = parseNum(tier.pricePerKm);
    if (isNaN(price) || price <= 0) errors[i].pricePerKm = "ต้องมากกว่า 0";
    if (isNaN(from) || from < 0) errors[i].fromKm = "ต้องเป็นตัวเลขตั้งแต่ 0";
    else if (i > 0) {
      const prevFrom = parseNum(d.tiers[i - 1].fromKm);
      if (!isNaN(prevFrom) && from <= prevFrom)
        errors[i].fromKm = `ต้องมากกว่าแถวก่อนหน้า (${prevFrom})`;
    }
    if (tier.toKm.trim() === "") {
      if (i !== d.tiers.length - 1)
        errors[i].toKm = "เว้นว่างได้เฉพาะแถวสุดท้าย (ขึ้นไป)";
    } else {
      const to = parseNum(tier.toKm);
      if (isNaN(to)) errors[i].toKm = "ต้องเป็นตัวเลข";
      else if (!isNaN(from) && to <= from) errors[i].toKm = "ต้องมากกว่า จาก";
    }
  });
  return errors;
};

export const hasTierErrors = (errors: ITierError[]): boolean =>
  errors.some((e) => !!(e.fromKm || e.toKm || e.pricePerKm));

// ว่างทั้งหมด → null (แอปผู้โดยสาร fallback ไปแสดง detail) — D4
// sentinel: tier สุดท้าย toKm ว่าง → null (ไม่ใช่ 0 — นั่นของ fareBands)
export const buildFareInfo = (d: IFareInfoDraft): IFareInfo | null => {
  const conditions = d.conditions.map((c) => c.trim()).filter((c) => c !== "");
  const startPriceText = d.startPriceText.trim();
  const trafficRateText = d.trafficRateText.trim();
  if (
    startPriceText === "" && trafficRateText === "" &&
    d.tiers.length === 0 && conditions.length === 0
  ) {
    return null;
  }
  return {
    startPriceText,
    rateTiers: d.tiers.map((t) => ({
      fromKm: Number(t.fromKm),
      toKm: t.toKm.trim() === "" ? null : Number(t.toKm),
      pricePerKm: Number(t.pricePerKm),
    })),
    trafficRateText,
    conditions,
  };
};

// hint ประกาศกำหนด — null = ไม่แสดง
export const legalCapHint = (
  min: number | null | undefined,
  max: number | null | undefined,
  unit: string
): string | null => {
  if (min != null && max != null) return `ประกาศกำหนด: ${min}–${max} ${unit}`;
  if (min != null) return `ประกาศกำหนดขั้นต่ำ: ${min} ${unit}`;
  if (max != null) return `ประกาศกำหนดสูงสุด: ${max} ${unit}`;
  return null;
};
